import { DateTime } from "luxon";
import { RRule, RRuleSet } from "rrule";
import {
	createRRuleSet,
	getNextNOccurrences,
	rruleTimeToLocalTime,
	timeToRRuleTime,
} from "../../../../../../common/repeat_utils";
import { time_format_utils } from "../../../../../../common/time_format_utils";
import RRuleGenerator from "../../../react-rrule-generator";
import { WithDropdownBase } from "../base_components/with_dropdown_base";
import { useEventCreationContext } from "../hooks/use_event_creation_context";
import { SelectableBox } from "../selectable_box";
import { SimpleSelectableBoxItem } from "../simple_selectable_box_item";
import { InfoTooltip } from "../tooltips/info_tooltip";
import { EditRepeatOccurrencesSection } from "./edit_repeat_occurrences_section";

function getRRuleString(rrule: RRule) {
	if (!rrule) {
		return "";
	}
	return rrule.toString();
}

function getNextDSTSwitchDate(local_now: DateTime) {
	const starting_month = local_now.month;
	const starting_offset = local_now.offset;

	for (let i = 0; i < 12; i++) {
		const current_month_index = starting_month + i + 1;

		const new_date_by_month = local_now.set({
			month: current_month_index,
			day: 1,
			hour: 0,
			minute: 0,
			second: 0,
		});

		// we found a differing offset at the start of a month, must've changed in previous month
		if (new_date_by_month.offset != starting_offset) {
			const previous_month = new_date_by_month.minus({
				months: 1,
			});

			for (let day = 0; day < previous_month.daysInMonth; day++) {
				const day_index = day + 1;

				const new_date_by_day = previous_month.set({
					day: day_index,
				});

				if (new_date_by_day.offset != starting_offset) {
					const previous_day = previous_month.set({
						day: day_index - 1,
					});

					for (let hour = 0; hour < 24; hour++) {
						const new_date_by_hour = previous_day.set({
							hour,
						});

						if (new_date_by_hour.offset != starting_offset) {
							return new_date_by_hour;
						}
					}
				}
			}
		}
	}

	return null;
}

function RepeatTZSelect(props: {
	start_time: DateTime;
	current_event_timezone: string;
	user_timezone: string;
	guild_timezone: string;
	previous_event_timezone: string;
	eventTimezoneUpdateFunc: (event_timezone: string) => void;
}) {
	const date_to_use = props.start_time ? props.start_time : DateTime.utc();

	const date = date_to_use.setZone(props.current_event_timezone);

	let DST_date: DateTime;
	if (!date.isOffsetFixed) {
		DST_date = getNextDSTSwitchDate(date);
		// if no date found, DST is actually fixed...
	}

	let info_note: JSX.Element;
	if (date.isOffsetFixed || !DST_date || !DST_date.isValid) {
		info_note = (
			<p>
				This event will be created using the{" "}
				<span className="emphasis">{props.current_event_timezone}</span>{" "}
				region which does not observe Daylight Savings Time.
			</p>
		);
	} else {
		const start_or_end_text = DST_date.isInDST ? "start" : "end";
		const date_text = DST_date.toLocaleString({
			dateStyle: "long",
		});

		info_note = (
			<p>
				This event will be created using the{" "}
				<span className="emphasis">{props.current_event_timezone}</span>{" "}
				region which observes Daylight Savings Time. DST will{" "}
				<span className="emphasis">{start_or_end_text}</span> on{" "}
				<span className="emphasis">{date_text}</span>. After this date,
				the event time will be automatically adjusted to maintain the
				same time of day in the{" "}
				<span className="emphasis">{props.current_event_timezone}</span>{" "}
				timezone.
			</p>
		);
	}

	const is_previous_timezone_different =
		props.previous_event_timezone &&
		props.previous_event_timezone !== props.guild_timezone &&
		props.previous_event_timezone !== props.user_timezone;

	return (
		<div className="flex flex-wrap gap-2">
			<InfoTooltip info={info_note}></InfoTooltip>
			<div>Timezone:</div>
			<div>
				{props.user_timezone === props.guild_timezone &&
				props.user_timezone === props.previous_event_timezone ? (
					<>{props.guild_timezone}</>
				) : (
					<select
						className="inline-dropdown"
						onChange={(e) =>
							props.eventTimezoneUpdateFunc(e.target.value)
						}
						value={props.current_event_timezone}
					>
						{is_previous_timezone_different && (
							<option value={props.previous_event_timezone}>
								{props.previous_event_timezone} (previous)
							</option>
						)}
						<option value={props.user_timezone}>
							{props.user_timezone} (local)
						</option>
						<option value={props.guild_timezone}>
							{props.guild_timezone} (server)
						</option>
					</select>
				)}
			</div>
		</div>
	);
}

function RecurringTimesDisplay(props: {
	start_time: DateTime;
	end_time: DateTime;
	rrule: RRule;
	guild_timezone: string;
	user_timezone: string;
	use_24h_clock: boolean;
}) {
	const { state } = useEventCreationContext();
	if (!props.rrule) {
		return <></>;
	}

	let repeating_guild_times: JSX.Element | undefined = undefined;
	let start_time = DateTime.local();

	const guild_timezone = props.guild_timezone;

	if (props.start_time) {
		start_time = props.start_time;

		if (guild_timezone) {
			let local_time_list_elements = [];
			let guild_time_list_elements = [];

			const rrule_utc_string = RRule.optionsToString(
				time_format_utils.transformRRuleToTimezone(
					state.rrule.origOptions,
					state.user_timezone,
					state.start_time
				)
			);

			const local_rrule_set = createRRuleSet({
				repeat_tz: state.event_timezone,
				rrule_str: rrule_utc_string,
				start_time_js: props.start_time.toJSDate(),
				exdates: state.exdates,
				rdates: state.rdates,
			});
			getNextNOccurrences(
				local_rrule_set,
				props.start_time.toJSDate(),
				state.event_timezone,
				4
			).forEach((date, index) => {
				const local_luxon = rruleTimeToLocalTime(date);

				const local_string = time_format_utils.formatTimeConciseV2(
					local_luxon,
					true
				);

				// let local_string = time_format_utils.format_time_concise(
				// 	DateTime.fromJSDate(date),
				// 	null,
				// 	props.use_24h_clock,
				// 	true
				// );

				local_time_list_elements.push(
					<li key={index}>{local_string}</li>
				);

				let guild_string = time_format_utils.formatTimeConciseV2(
					local_luxon.setZone(guild_timezone),
					false
				);

				guild_time_list_elements.push(
					<li key={index}>{guild_string}</li>
				);
			});

			let subsequent_event_display_string;
			if (props.end_time) {
				subsequent_event_display_string =
					"Subsequent events will be posted when the previous one ends.";
			} else {
				subsequent_event_display_string = `Subsequent events will be posted 1 hour after the previous event begins.`;
			}

			if (props.user_timezone === guild_timezone) {
				repeating_guild_times = (
					<div>
						<div className="columns">
							<div className="column is-half">
								<div>
									<b>
										Event Repeat Times (
										{time_format_utils.get_timezone_abbr(
											start_time.setZone(
												props.user_timezone
											),
											props.user_timezone
										)}
										)
									</b>
									<br />
								</div>
								<ul>
									{local_time_list_elements}
									<li key="ellipsis">...</li>
								</ul>
							</div>
							<br></br>
						</div>
						<p>{subsequent_event_display_string}</p>
					</div>
				);
			} else {
				repeating_guild_times = (
					<div>
						<div className="columns">
							<div className="column is-half">
								<div>
									<b>
										Local Repeat Times (
										{time_format_utils.get_timezone_abbr(
											start_time.setZone(
												props.user_timezone
											),
											props.user_timezone
										)}
										)
									</b>
									<br />
								</div>
								<ul>
									{local_time_list_elements}
									<li key="ellipsis">...</li>
								</ul>
							</div>
							<div className="column is-half">
								<div>
									<b>
										Server Repeat Times (
										{time_format_utils.get_timezone_abbr(
											start_time.setZone(guild_timezone),
											guild_timezone
										)}
										)
									</b>
									<br />
								</div>
								<ul>
									{guild_time_list_elements}
									<li key="ellipsis">...</li>
								</ul>
							</div>
							<br></br>
						</div>
						<p>{subsequent_event_display_string}</p>
					</div>
				);
			}
		}
	}

	if (props.start_time && repeating_guild_times) {
		return <div className="note">{repeating_guild_times}</div>;
	} else {
		return <></>;
	}
}

export default function RepeatForm(props: {
	initial_value: RRule;
	start_time: DateTime;
	end_time: DateTime;
	event_timezone: string;
	guild_timezone: string;
	user_timezone: string;
	use_24h_clock: boolean;
	previous_event_timezone: string;
	allow_maintaining_rsvp: boolean;
	rruleUpdateFunc: (rrule: RRule) => void;
	maintainRSVPUpdateFunc: (should_maintain: boolean) => void;
	eventTimezoneUpdateFunc: (timezone: string) => void;
	should_hide_time_listing?: boolean;
}) {
	return (
		<div>
			<RepeatTZSelect
				start_time={props.start_time}
				user_timezone={props.user_timezone}
				guild_timezone={props.guild_timezone}
				current_event_timezone={props.event_timezone}
				previous_event_timezone={props.previous_event_timezone}
				eventTimezoneUpdateFunc={props.eventTimezoneUpdateFunc}
			/>
			<RRuleGenerator
				onChange={(val) =>
					props.rruleUpdateFunc(val ? RRule.fromString(val) : null)
				}
				value={getRRuleString(props.initial_value)}
			/>
			<EditRepeatOccurrencesSection />
			<div className="py-3">
				<input
					type="checkbox"
					name="has_reminders"
					checked={props.allow_maintaining_rsvp}
					onChange={(e) =>
						props.maintainRSVPUpdateFunc(e.target.checked)
					}
				/>{" "}
				Allow users to maintain RSVP for future events
			</div>
			{!props.should_hide_time_listing && (
				<RecurringTimesDisplay
					start_time={props.start_time}
					end_time={props.end_time}
					guild_timezone={props.guild_timezone}
					rrule={props.initial_value}
					use_24h_clock={props.use_24h_clock}
					user_timezone={props.user_timezone}
				/>
			)}
		</div>
	);
}
